/**
 * time_clock_controller.js — attach the device's clock to a time tap (Stimulus).
 *
 * This controller exists for one field. Every other fact about a time entry —
 * who, which visit, which rate — the server knows or can look up. The one fact
 * only the phone holds is WHEN THE BUTTON WAS PRESSED, and it matters most
 * exactly when the server cannot be reached: a technician who clocks on at
 * seven in a plant room with no signal and syncs at five worked ten hours, and
 * a server timestamp would silently record none of them.
 *
 * So each form carries a hidden `at`, filled the instant the form is submitted
 * rather than when the page rendered — a job page left open on a bench for an
 * hour would otherwise stamp every tap with the time it was opened.
 *
 * There is deliberately no ticking display. The elapsed figure is arithmetic
 * over two stored columns, and a timer running in the browser would be a
 * second copy of it, drifting, with nothing to reconcile it against.
 *
 * A tap changes more than the card it was made on: clocking on from a job page
 * changes the dashboard's clock card and the My time page too. Once the server
 * has taken a tap, the device's saved copies of the pages named in
 * data-time-clock-pages-value are brought up to date
 * (window.chippiePageCheck.refreshCopy), so a technician who then loses signal
 * and opens the dashboard sees Clock off, not the Clock on saved before. The
 * page the tap was made on updates itself through its Turbo Stream. A tap
 * queued offline never reaches here: offline_tap_controller.js shows it on
 * every card it changes until it syncs.
 *
 * Markup contract inside the root:
 *   [data-time-clock-target="at"]   a hidden input in each form
 *   data-action="time-clock#submit" on each form
 *   data-time-clock-pages-value     the URLs of the other pages a tap changes
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ['at'];
    static values = { pages: Array };

    connect() {
        this._onSubmitEnd = (event) => this._refreshOtherPages(event);
        this.element.addEventListener('turbo:submit-end', this._onSubmitEnd);
    }

    disconnect() {
        this.element.removeEventListener('turbo:submit-end', this._onSubmitEnd);
    }

    _refreshOtherPages(event) {
        if (!event.detail || !event.detail.success || !window.chippiePageCheck) return;
        this.pagesValue
            .filter((url) => url !== window.location.pathname)
            .forEach((url) => window.chippiePageCheck.refreshCopy(url));
    }

    submit(event) {
        const form = event.target.closest('form');
        if (!form) return;
        const at = form.querySelector('[data-time-clock-target="at"]');
        // ISO 8601 with the offset the device believes it is in. The server
        // decides how far to trust it; it never has to guess what it meant.
        if (at) at.value = new Date().toISOString();
    }
}
